/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [leave-space-dialog.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 05.04.20, 18:41
 */

import React, {useState} from 'react';
import {useAuthorization} from "../../selectors";
import {useLanguage} from "../configuration/configuration";
import {NONE_STATUS, notifyGalleryChange, notifyPageChange, PENDING_STATUS, SUCCESS_STATUS} from "../../actions";
import {Spinner} from "../util/spinner";
import {Close} from "@material-ui/icons";
import {Button} from "../navigation/buttons/nav-buttons";
import {NavigationGroup, NavigationRow} from "../navigation/layout/nav-layout";
import {FormRow} from "../forms/manage-spaces/manage-space-form";
import FormMarkdownTextarea from "../toggler/form-markdown-textarea";
import useUpdateSpace from "../hooks/use-update-space";
import {notify} from "../notifications/notify";
import {useDispatch} from "react-redux";
import {ONLY_ME_ACCESS, PRIVATE_ACCESS, PUBLIC_ACCESS, RESTRICTED_ACCESS} from "../../actions/spaces";

/**
 * only an authorized user can create galleries and only for himself
 * a GALLERY is just a stripped down GENERIC space of type GALLERY
 * update handles name and description changes only - images must be done on the page itself
 */
const CreateGalleryPopover = ({spaceId, access= '', name = '', description ='',
                                  subType = null, onCreateBindToSpace = false,
                                  onUpdate, onClose}) => {
    const [formData, setFormData] = useState({
        type: 'GALLERY',
        subType: null, // do not update if null
        access: access,
        fees: 'NONE',
        hygiene: 'NONE',
        ranking: 100,
        name: name,
        description: description});
    const [invalid, setInvalid] = useState(false);
    const {updateSpace, createSpace: createSpaceByUser, createSpaceBySpace, space, status, error} = useUpdateSpace();
    const {isAuthorized} = useAuthorization();
    const Lang = useLanguage();
    const dispatch = useDispatch();


    const updateGallerySpace = () => {
        updateSpace(spaceId, formData, (updatedSpace) => {
            notify(Lang.gallery.popover.updatedNotification)
            dispatch(notifyGalleryChange('UPDATE', updatedSpace.id, updatedSpace))
            onUpdate && onUpdate(formData);
        });
    }

    // create gallery space by user
    const createGalleryByUser = () => {
        const updatedFormData = {...formData, subType: 'DEFAULT'}

        createSpaceByUser(updatedFormData, (newSpace) => {
            notify(Lang.gallery.popover.createdNotification(formData.name))
            setFormData({...formData, name: '', description: ''});
            dispatch(notifyGalleryChange('CREATE', newSpace.id, newSpace))
            onClose();
        });
    }

    // create gallery by group space
    const createGalleryBySpace = () => {
        const updatedFormData = {...formData, subType: 'GROUP'}
        createSpaceBySpace(spaceId, updatedFormData, (newSpace) => {
            notify(Lang.gallery.popover.createdNotification(formData.name))
            setFormData({...formData, name: '', description: ''});
            dispatch(notifyGalleryChange('CREATE', newSpace.id, newSpace))
            onClose();
        });
    }

    const handleOnChange = (event) => {
        setFormData({...formData, [event.target.name]: event.target.value});
    }

    const handleSubmit = (event) => {
        event.target.classList.add('was-validated');
        event.preventDefault();
        event.stopPropagation();

        if (!event.target.checkValidity()) {
            setInvalid(true);
            return;
        }
        setInvalid(false);

        if(spaceId) {
            onCreateBindToSpace ? createGalleryBySpace() : updateGallerySpace();

        } else {
            createGalleryByUser();
        }
    }

    if (status === PENDING_STATUS) return <Spinner/>
    if (!isAuthorized) return null;

    return <div className='px-2 py-1'>
        <NavigationRow>
            <NavigationGroup>
                <h4 className='clr-popover-search'>{spaceId ? Lang.gallery.popover.update : Lang.gallery.popover.create}</h4>
            </NavigationGroup>
            <NavigationGroup>
                <button className='btn btn-outline-primary m-0 p-1' onClick={onClose}>
                    <Close className='clr-popover-search'/>
                </button>
            </NavigationGroup>
        </NavigationRow>


        <form noValidate className={`needs-validation ${invalid ? 'form-invalid' : ''} text-start`} onSubmit={handleSubmit}>
            <label className="form-check-label"
                   htmlFor="publicAccessId">{Lang.gallery.popover.access.text}</label>
            <FormRow className="form-check-label ">
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="access"
                           checked={formData.access === PUBLIC_ACCESS}
                           onChange={handleOnChange}
                           id="publicAccessId" value={PUBLIC_ACCESS} required/>
                    <label className="form-check-label"
                           htmlFor="publicAccessId">{Lang.updateSpace.access.public}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="access"
                           checked={formData.access === PRIVATE_ACCESS}
                           onChange={handleOnChange}
                           id="privateId" value={PRIVATE_ACCESS}/>
                    <label className="form-check-label"
                           htmlFor="privateId">{Lang.updateSpace.access.private}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="access"
                           checked={formData.access === RESTRICTED_ACCESS}
                           onChange={handleOnChange}
                           id="restrictedId" value={RESTRICTED_ACCESS}/>
                    <label className="form-check-label"
                           htmlFor="restrictedId">{Lang.updateSpace.access.restricted}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="access"
                           checked={formData.access === ONLY_ME_ACCESS}
                           onChange={handleOnChange}
                           id="restrictedId" value={ONLY_ME_ACCESS}/>
                    <label className="form-check-label"
                           htmlFor="restrictedId">{Lang.updateSpace.access.onlyMe}</label>
                </div>
            </FormRow>


            <FormRow className='mb-2'>
                <label className="form-check-label float-left"
                       htmlFor="nameId">{Lang.updateSpace.name.text}</label>
                <input className='form-control' type="text" name="name"
                       id='nameId'
                       placeholder={Lang.updateSpace.name.placeholder}
                       value={formData.name}
                       onChange={handleOnChange} required/>
            </FormRow>

            <FormMarkdownTextarea className='form-control' name="description"
                                  label={Lang.updateSpace.description.text}
                                  placeholder={Lang.updateSpace.description.placeholder}
                                  id='descriptionId'
                                  value={formData.description}
                                  maxLength='512'
                                  onChange={handleOnChange} required/>

            <FormRow className='mt-2 mb-2'>
                <Button block
                        type="submit">{spaceId ? Lang.gallery.popover.update : Lang.gallery.popover.create}</Button>
            </FormRow>

        </form>
    </div>
};

export default CreateGalleryPopover;